import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Avatar,
  Stack,
  Chip,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CampaignIcon from "@mui/icons-material/Campaign";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AutomationIcon from "@mui/icons-material/Sync";

export default function BenefitsComponent({ theme }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />,
      title: "Lucratividade por Pedido",
      description:
        "Monitoramento em tempo real da lucratividade de cada pedido com alertas inteligentes e relatórios detalhados.",
      color: "#38A169",
      gradient: "linear-gradient(135deg, #38A169 0%, #2F855A 100%)",
      stats: "+40% lucro médio",
    },
    {
      icon: <InventoryIcon sx={{ fontSize: 32 }} />,
      title: "Gestão Inteligente de Estoque",
      description:
        "Previsão de demanda com IA, reposição automática e otimização de capital de giro para máxima eficiência.",
      color: "#3182CE",
      gradient: "linear-gradient(135deg, #3182CE 0%, #2C5282 100%)",
      stats: "-60% ruptura",
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 32 }} />,
      title: "Precificação Dinâmica",
      description:
        "Algoritmos avançados que ajustam preços automaticamente baseados em concorrência, demanda e margem desejada.",
      color: "#D69E2E",
      gradient: "linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)",
      stats: "+25% margem",
    },
    {
      icon: <CampaignIcon sx={{ fontSize: 32 }} />,
      title: "Promoções Inteligentes",
      description:
        "Análise automática e aceite de promoções com base em ROI, histórico de performance e objetivos estratégicos.",
      color: "#E53E3E",
      gradient: "linear-gradient(135deg, #E53E3E 0%, #C53030 100%)",
      stats: "+150% ROI",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      title: "Monitoramento 24/7",
      description:
        "Vigilância contínua de todas as operações com alertas instantâneos para anomalias e oportunidades.",
      color: "#805AD5",
      gradient: "linear-gradient(135deg, #805AD5 0%, #6B46C1 100%)",
      stats: "99.9% uptime",
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      title: "Analytics Avançado",
      description:
        "Dashboards interativos com insights acionáveis, previsões de tendências e métricas de performance.",
      color: "#FF7323",
      gradient: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
      stats: "200+ métricas",
    },
  ];

  const stats = [
    { label: "Empresas Atendidas", value: "1", icon: "🏢" },
    { label: "Faturamento Gerenciado", value: "R$ 100M+", icon: "💰" },
    { label: "Pedidos Processados", value: "200K+", icon: "📦" },
    { label: "Economia Gerada", value: "R$ 150K+", icon: "📈" },
  ];

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        background: `
          linear-gradient(135deg, 
            rgba(255, 255, 255, 1) 0%, 
            rgba(248, 250, 252, 1) 50%, 
            rgba(237, 242, 247, 1) 100%
          )
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: -200,
          width: 400,
          height: 400,
          background:
            "radial-gradient(circle, rgba(255, 115, 35, 0.05) 0%, transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            transform: isVisible ? "translateY(0)" : "translateY(50px)",
            opacity: isVisible ? 1 : 0,
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Chip
            label="🎯 Resultados Comprovados"
            sx={{
              mb: 3,
              backgroundColor: "rgba(56, 161, 105, 0.1)",
              color: theme.palette.success.main,
              fontWeight: 600,
              fontSize: "1rem",
              padding: "12px 20px",
              borderRadius: "50px",
              border: "1px solid rgba(56, 161, 105, 0.2)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3.2rem" },
              fontWeight: 800,
              mb: 3,
              background:
                "linear-gradient(135deg, #38A169 0%, #2F855A 50%, #FF7323 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Potencialize Seus Resultados
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 6,
            }}
          >
            Nossa <strong>inteligência artificial</strong> trabalha 24/7 para otimizar cada aspecto do seu negócio, gerando{" "}
            <strong>resultados mensuráveis e sustentáveis</strong>.
          </Typography>

          {/* Stats Row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 6,
              justifyContent: 'center'
            }}
          >
            {stats.map((stat, index) => (
              <Box key={index} sx={{ flex: '1 1 250px', minWidth: '200px', maxWidth: '300px' }}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: "16px",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 115, 35, 0.1)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                    },
                  }}
                >
                  <Typography variant="h3" sx={{ fontSize: "2rem", mb: 1 }}>
                    {stat.icon}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Benefits Grid */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center'
          }}
        >
          {benefits.map((benefit, index) => (
            <Box key={index} sx={{ flex: '1 1 350px', minWidth: '300px', maxWidth: '400px' }}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: "24px",
                  border: "1px solid rgba(255, 115, 35, 0.1)",
                  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  transform: isVisible
                    ? "translateY(0)"
                    : "translateY(50px)",
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: `${index * 100}ms`,
                  "&:hover": {
                    transform: "translateY(-12px)",
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
                    "& .benefit-icon": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                    "& .stats-chip": {
                      transform: "scale(1.05)",
                    },
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Icon Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 3,
                    }}
                  >
                    <Avatar
                      className="benefit-icon"
                      sx={{
                        width: 72,
                        height: 72,
                        background: benefit.gradient,
                        color: "white",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {benefit.icon}
                    </Avatar>

                    <Chip
                      className="stats-chip"
                      label={benefit.stats}
                      sx={{
                        backgroundColor: `${benefit.color}15`,
                        color: benefit.color,
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: theme.palette.text.primary,
                        fontSize: "1.3rem",
                      }}
                    >
                      {benefit.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                        fontSize: "1rem",
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Bottom CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            mt: 10,
            p: 6,
            borderRadius: "24px",
            background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
            }}
          >
            Pronto para Revolucionar Seu E-commerce?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              mb: 4,
              fontSize: { xs: "1.1rem", md: "1.3rem" },
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Junte-se a centenas de empresas que já transformaram seus resultados
            com a Wala Smart Work
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Chip
              icon={<SpeedIcon />}
              label="Implementação em 24h"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: "1rem",
                padding: "12px 16px",
              }}
            />
            <Chip
              icon={<SecurityIcon />}
              label="Suporte 24/7"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: "1rem",
                padding: "12px 16px",
              }}
            />
            <Chip
              icon={<AutomationIcon />}
              label="Sem compromisso"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                fontWeight: 600,
                fontSize: "1rem",
                padding: "12px 16px",
              }}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
